import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchJson, postJson } from '../lib/api';

// --- Types ---

export interface RenewalDeal {
  id: string;
  dealId: string;
  dealName: string;
  organizationId?: string;
  organizationName?: string;
  pipelineId?: string;
  pipelineName?: string;
  expirationDate: string;
  daysRemaining: number;
  premium: number;
  status: 'upcoming' | 'in_progress' | 'renewed' | 'lost' | 'expired';
  assigneeId?: string;
  assigneeName?: string;
  contactName?: string;
  policyNumber?: string;
  carrier?: string;
  createdAt: string;
  updatedAt: string;
}

export interface UpcomingRenewalsResponse {
  renewals: RenewalDeal[];
  total: number;
  summary: {
    totalRenewals: number;
    totalPremiumAtRisk: number;
    avgDaysToExpiry: number;
    urgentCount: number;
    warningCount: number;
    healthyCount: number;
  };
}

export interface RenewalFilters {
  daysAhead?: number;
  status?: string;
  organizationId?: string;
  pipelineId?: string;
}

// --- Helpers ---

function buildQueryString(filters?: RenewalFilters): string {
  if (!filters) return '';
  const params = new URLSearchParams();
  if (filters.daysAhead) params.set('daysAhead', String(filters.daysAhead));
  if (filters.status) params.set('status', filters.status);
  if (filters.organizationId) params.set('organizationId', filters.organizationId);
  if (filters.pipelineId) params.set('pipelineId', filters.pipelineId);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// --- Hooks ---

export function useUpcomingRenewals(filters?: RenewalFilters) {
  return useQuery({
    queryKey: ['renewals-upcoming', filters],
    queryFn: () =>
      fetchJson<UpcomingRenewalsResponse>(`/api/renewals/upcoming${buildQueryString(filters)}`),
  });
}

export function useCreateRenewalDeal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dealId: string) =>
      postJson<RenewalDeal>(`/api/renewals/${dealId}/create-renewal`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['renewals-upcoming'] });
      queryClient.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}

export function useProcessRenewalReminders() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => postJson<{ processed: number }>('/api/renewals/process', {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['renewals-upcoming'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}
